import React, { useState, useEffect } from 'react';
import { X, Trash2, MessageSquare, Send, Link2, Target, Flag, Palette, Package, AlertTriangle, ArrowRight, Plus, Sparkles, Loader2 } from 'lucide-react';
import api from '../api';
import { AIInsightsPanel } from './AIInsightsPanel';

const TABS = [
  { key: 'details', label: 'Details' },
  { key: 'scoring', label: 'RICE' },
  { key: 'wsjf', label: 'WSJF' },
  { key: 'dependencies', label: 'Dependencies', icon: Link2 },
  { key: 'comments', label: 'Comments', icon: MessageSquare },
  { key: 'ai', label: 'AI', icon: Sparkles },
];

function ItemDrawer({ item, onClose, onUpdate, onDelete, allItems = [], roadmapId }) {
  const [editedItem, setEditedItem] = useState(item);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [activeTab, setActiveTab] = useState('details');
  const [dependencies, setDependencies] = useState([]);
  const [goals, setGoals] = useState([]);
  const [okrs, setOKRs] = useState([]);
  const [themes, setThemes] = useState([]);
  const [releases, setReleases] = useState([]);
  const [showAddDep, setShowAddDep] = useState(false);
  const [newDepItemId, setNewDepItemId] = useState('');
  const [newDepType, setNewDepType] = useState('blocks');
  const [generatingDesc, setGeneratingDesc] = useState(false);

  useEffect(() => {
    setEditedItem(item);
    loadStrategyData();
  }, [item._id]);

  useEffect(() => {
    if (activeTab === 'comments') loadComments();
    if (activeTab === 'dependencies') loadDependencies();
  }, [activeTab, item._id]);

  const loadStrategyData = async () => {
    try {
      const [g, o, t, r] = await Promise.all([api.getGoals(), api.getOKRs(), api.getThemes(), api.getReleases()]);
      setGoals(g); setOKRs(o); setThemes(t); setReleases(r);
    } catch (e) { console.error(e); }
  };

  const loadComments = async () => {
    try { const data = await api.getComments(item._id); setComments(data); } catch (e) { console.error(e); }
  };

  const loadDependencies = async () => {
    try { const data = await api.getItemDependencies(item._id); setDependencies(data); } catch (e) { console.error(e); }
  };

  const handleSave = () => { onUpdate(editedItem); };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    try { await api.createComment({ item_id: item._id, content: newComment, mentions: [] }); setNewComment(''); loadComments(); } catch (e) { console.error(e); }
  };

  const handleAddDependency = async () => {
    if (!newDepItemId) return;
    try {
      await api.createDependency({ from_item_id: item._id, to_item_id: newDepItemId, type: newDepType });
      setShowAddDep(false); setNewDepItemId(''); loadDependencies();
    } catch (e) { console.error(e); }
  };

  const handleDeleteDep = async (depId) => {
    try { await api.deleteDependency(depId); loadDependencies(); } catch (e) { console.error(e); }
  };

  const calculateRiceScore = () => {
    const { reach, impact, confidence, effort } = editedItem;
    if (reach && impact && confidence && effort) {
      setEditedItem({ ...editedItem, rice_score: (reach * impact * confidence) / effort });
    }
  };

  const calculateWsjf = () => {
    const { business_value, time_criticality, risk_reduction, job_size } = editedItem;
    if (business_value && time_criticality && risk_reduction && job_size) {
      setEditedItem({ ...editedItem, wsjf_score: (business_value + time_criticality + risk_reduction) / job_size });
    }
  };

  const handleGenerateDescription = async () => {
    if (!editedItem.title?.trim()) return;
    setGeneratingDesc(true);
    try {
      const data = await api.request('/api/ai/generate-description', {
        method: 'POST',
        body: JSON.stringify({ title: editedItem.title, context: editedItem.type }),
      });
      setEditedItem({ ...editedItem, description: data.description });
    } catch (e) {
      console.error(e);
    } finally {
      setGeneratingDesc(false);
    }
  };

  const otherItems = allItems.filter(i => i._id !== item._id);

  const getItemTitle = (id) => {
    const found = allItems.find(i => i._id === id);
    return found ? found.title : id;
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-end z-50" data-testid="item-drawer" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-card w-full max-w-2xl border-l border-border overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 bg-card/95 backdrop-blur-md border-b border-border p-5 flex items-center justify-between z-10">
          <div>
            <h2 className="text-lg font-semibold">Edit Item</h2>
            <p className="text-xs text-muted-foreground mt-0.5 truncate max-w-[300px]">{item.title}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-secondary rounded-lg transition-colors" data-testid="close-drawer">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-border px-5 overflow-x-auto">
          <div className="flex gap-1 min-w-max">
            {TABS.map(tab => (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                className={`py-3 px-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap flex items-center gap-1.5 ${activeTab === tab.key ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
                data-testid={`${tab.key}-tab`}>
                {tab.icon && <tab.icon className="h-3.5 w-3.5" />}
                {tab.label}
                {tab.key === 'comments' && comments.length > 0 && <span className="text-xs bg-muted px-1.5 py-0.5 rounded-full">{comments.length}</span>}
                {tab.key === 'dependencies' && dependencies.length > 0 && <span className="text-xs bg-muted px-1.5 py-0.5 rounded-full">{dependencies.length}</span>}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-5">
          {activeTab === 'details' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1.5">Title</label>
                <input type="text" value={editedItem.title} onChange={(e) => setEditedItem({ ...editedItem, title: e.target.value })} className="w-full px-4 py-2.5 border border-input rounded-xl bg-background focus:outline-none focus:ring-2 focus:ring-ring text-sm" data-testid="edit-title" />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-sm font-medium">Description</label>
                  <button
                    type="button"
                    onClick={handleGenerateDescription}
                    disabled={generatingDesc || !editedItem.title?.trim()}
                    className="flex items-center gap-1.5 text-xs px-2.5 py-1 bg-primary/10 text-primary border border-primary/20 rounded-lg hover:bg-primary/20 transition-colors disabled:opacity-40"
                    data-testid="ai-describe-button"
                  >
                    {generatingDesc ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
                    AI Describe
                  </button>
                </div>
                <textarea value={editedItem.description || ''} onChange={(e) => setEditedItem({ ...editedItem, description: e.target.value })} rows={3} className="w-full px-4 py-2.5 border border-input rounded-xl bg-background focus:outline-none focus:ring-2 focus:ring-ring text-sm" data-testid="edit-description" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1.5">Type</label>
                  <select value={editedItem.type} onChange={(e) => setEditedItem({ ...editedItem, type: e.target.value })} className="w-full px-4 py-2.5 border border-input rounded-xl bg-background focus:outline-none focus:ring-2 focus:ring-ring text-sm" data-testid="edit-type">
                    <option value="initiative">Initiative</option><option value="epic">Epic</option><option value="feature">Feature</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1.5">Status</label>
                  <select value={editedItem.status} onChange={(e) => setEditedItem({ ...editedItem, status: e.target.value })} className="w-full px-4 py-2.5 border border-input rounded-xl bg-background focus:outline-none focus:ring-2 focus:ring-ring text-sm" data-testid="edit-status">
                    <option value="planned">Planned</option><option value="in-progress">In Progress</option><option value="done">Done</option><option value="on-hold">On Hold</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1.5">Priority</label>
                  <select value={editedItem.priority} onChange={(e) => setEditedItem({ ...editedItem, priority: e.target.value })} className="w-full px-4 py-2.5 border border-input rounded-xl bg-background focus:outline-none focus:ring-2 focus:ring-ring text-sm" data-testid="edit-priority">
                    <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option><option value="critical">Critical</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1.5">Timeframe</label>
                  <select value={editedItem.timeframe} onChange={(e) => setEditedItem({ ...editedItem, timeframe: e.target.value })} className="w-full px-4 py-2.5 border border-input rounded-xl bg-background focus:outline-none focus:ring-2 focus:ring-ring text-sm" data-testid="edit-timeframe">
                    <option value="now">Now</option><option value="next">Next</option><option value="later">Later</option><option value="Q1">Q1</option><option value="Q2">Q2</option><option value="Q3">Q3</option><option value="Q4">Q4</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1.5">Start Date</label>
                  <input type="date" value={editedItem.start_date ? editedItem.start_date.split('T')[0] : ''} onChange={(e) => setEditedItem({ ...editedItem, start_date: e.target.value })} className="w-full px-4 py-2.5 border border-input rounded-xl bg-background focus:outline-none focus:ring-2 focus:ring-ring text-sm" data-testid="edit-start-date" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1.5">End Date</label>
                  <input type="date" value={editedItem.end_date ? editedItem.end_date.split('T')[0] : ''} onChange={(e) => setEditedItem({ ...editedItem, end_date: e.target.value })} className="w-full px-4 py-2.5 border border-input rounded-xl bg-background focus:outline-none focus:ring-2 focus:ring-ring text-sm" data-testid="edit-end-date" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5">Owner</label>
                <input type="text" value={editedItem.owner || ''} onChange={(e) => setEditedItem({ ...editedItem, owner: e.target.value })} className="w-full px-4 py-2.5 border border-input rounded-xl bg-background focus:outline-none focus:ring-2 focus:ring-ring text-sm" placeholder="email@company.com" data-testid="edit-owner" />
              </div>

              {/* Strategy Linkage */}
              <div className="pt-3 border-t border-border">
                <h3 className="text-sm font-semibold mb-3 flex items-center gap-2"><Target className="h-4 w-4 text-primary" /> Strategic Context</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium mb-1 text-muted-foreground">Goal</label>
                    <select value={editedItem.goal_id || ''} onChange={(e) => setEditedItem({ ...editedItem, goal_id: e.target.value || null })} className="w-full px-3 py-2 border border-input rounded-xl bg-background focus:outline-none focus:ring-2 focus:ring-ring text-xs" data-testid="edit-goal">
                      <option value="">None</option>
                      {goals.map(g => <option key={g._id} value={g._id}>{g.title}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1 text-muted-foreground">OKR</label>
                    <select value={editedItem.okr_id || ''} onChange={(e) => setEditedItem({ ...editedItem, okr_id: e.target.value || null })} className="w-full px-3 py-2 border border-input rounded-xl bg-background focus:outline-none focus:ring-2 focus:ring-ring text-xs" data-testid="edit-okr">
                      <option value="">None</option>
                      {okrs.map(o => <option key={o._id} value={o._id}>{o.title}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1 text-muted-foreground">Theme</label>
                    <select value={editedItem.theme_id || ''} onChange={(e) => setEditedItem({ ...editedItem, theme_id: e.target.value || null })} className="w-full px-3 py-2 border border-input rounded-xl bg-background focus:outline-none focus:ring-2 focus:ring-ring text-xs" data-testid="edit-theme">
                      <option value="">None</option>
                      {themes.map(t => <option key={t._id} value={t._id}>{t.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1 text-muted-foreground">Release</label>
                    <select value={editedItem.release_id || ''} onChange={(e) => setEditedItem({ ...editedItem, release_id: e.target.value || null })} className="w-full px-3 py-2 border border-input rounded-xl bg-background focus:outline-none focus:ring-2 focus:ring-ring text-xs" data-testid="edit-release">
                      <option value="">None</option>
                      {releases.map(r => <option key={r._id} value={r._id}>{r.name}{r.version ? ` v${r.version}` : ''}</option>)}
                    </select>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'scoring' && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">RICE: (Reach x Impact x Confidence) / Effort</p>
              <div className="grid grid-cols-2 gap-3">
                {[{ key: 'reach', label: 'Reach', ph: '0-10' }, { key: 'impact', label: 'Impact', ph: '0-10' }, { key: 'confidence', label: 'Confidence %', ph: '0-100' }, { key: 'effort', label: 'Effort (person-months)', ph: '1+' }].map(f => (
                  <div key={f.key}>
                    <label className="block text-sm font-medium mb-1.5">{f.label}</label>
                    <input type="number" value={editedItem[f.key] || ''} onChange={(e) => setEditedItem({ ...editedItem, [f.key]: parseInt(e.target.value) || 0 })} className="w-full px-4 py-2.5 border border-input rounded-xl bg-background focus:outline-none focus:ring-2 focus:ring-ring text-sm font-mono-score" placeholder={f.ph} data-testid={`edit-${f.key}`} />
                  </div>
                ))}
              </div>
              <button onClick={calculateRiceScore} className="w-full py-2.5 bg-primary text-primary-foreground rounded-xl hover:opacity-90 font-semibold btn-press transition-opacity" data-testid="calculate-rice">Calculate RICE Score</button>
              {editedItem.rice_score != null && (
                <div className="p-5 bg-primary/10 border border-primary/20 rounded-xl text-center">
                  <p className="text-xs text-muted-foreground mb-1">RICE Score</p>
                  <p className="text-3xl font-bold font-mono-score text-primary" data-testid="rice-score-display">{editedItem.rice_score.toFixed(2)}</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'wsjf' && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">WSJF: (Business Value + Time Criticality + Risk Reduction) / Job Size</p>
              <div className="grid grid-cols-2 gap-3">
                {[{ key: 'business_value', label: 'Business Value (1-10)' }, { key: 'time_criticality', label: 'Time Criticality (1-10)' }, { key: 'risk_reduction', label: 'Risk Reduction (1-10)' }, { key: 'job_size', label: 'Job Size (1-10)' }].map(f => (
                  <div key={f.key}>
                    <label className="block text-sm font-medium mb-1.5">{f.label}</label>
                    <input type="number" value={editedItem[f.key] || ''} onChange={(e) => setEditedItem({ ...editedItem, [f.key]: parseInt(e.target.value) || 0 })} className="w-full px-4 py-2.5 border border-input rounded-xl bg-background focus:outline-none focus:ring-2 focus:ring-ring text-sm font-mono-score" placeholder="1-10" data-testid={`edit-${f.key}`} />
                  </div>
                ))}
              </div>
              <button onClick={calculateWsjf} className="w-full py-2.5 bg-primary text-primary-foreground rounded-xl hover:opacity-90 font-semibold btn-press transition-opacity" data-testid="calculate-wsjf">Calculate WSJF Score</button>
              {editedItem.wsjf_score != null && (
                <div className="p-5 bg-primary/10 border border-primary/20 rounded-xl text-center">
                  <p className="text-xs text-muted-foreground mb-1">WSJF Score</p>
                  <p className="text-3xl font-bold font-mono-score text-primary" data-testid="wsjf-score-display">{editedItem.wsjf_score.toFixed(2)}</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'dependencies' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">Manage blocking relationships</p>
                <button onClick={() => setShowAddDep(true)} className="px-3 py-1.5 bg-primary text-primary-foreground rounded-lg text-xs font-semibold flex items-center gap-1 hover:opacity-90" data-testid="add-dependency-button">
                  <Plus className="h-3 w-3" /> Add
                </button>
              </div>

              {showAddDep && (
                <div className="p-4 bg-muted/50 rounded-xl border border-border space-y-3" data-testid="add-dep-form">
                  <div>
                    <label className="block text-xs font-medium mb-1">Relationship</label>
                    <select value={newDepType} onChange={(e) => setNewDepType(e.target.value)} className="w-full px-3 py-2 border border-input rounded-lg bg-background text-sm" data-testid="dep-type-select">
                      <option value="blocks">This item blocks...</option>
                      <option value="blocked_by">This item is blocked by...</option>
                      <option value="relates_to">Relates to...</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">Item</label>
                    <select value={newDepItemId} onChange={(e) => setNewDepItemId(e.target.value)} className="w-full px-3 py-2 border border-input rounded-lg bg-background text-sm" data-testid="dep-item-select">
                      <option value="">Select an item...</option>
                      {otherItems.map(i => <option key={i._id} value={i._id}>{i.title}</option>)}
                    </select>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={handleAddDependency} className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-xs font-semibold" data-testid="confirm-add-dep">Add</button>
                    <button onClick={() => setShowAddDep(false)} className="px-4 py-2 bg-secondary text-secondary-foreground rounded-lg text-xs">Cancel</button>
                  </div>
                </div>
              )}

              {dependencies.length === 0 && !showAddDep ? (
                <div className="text-center py-10">
                  <Link2 className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
                  <p className="text-sm text-muted-foreground">No dependencies yet</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {dependencies.map(dep => {
                    const isFrom = dep.from_item_id === item._id;
                    const otherId = isFrom ? dep.to_item_id : dep.from_item_id;
                    const label = isFrom ? (dep.type === 'blocks' ? 'Blocks' : dep.type === 'relates_to' ? 'Relates to' : 'Blocked by') : (dep.type === 'blocks' ? 'Blocked by' : dep.type === 'relates_to' ? 'Relates to' : 'Blocks');
                    return (
                      <div key={dep._id} className="flex items-center justify-between p-3 bg-muted/30 border border-border rounded-lg group" data-testid={`dep-${dep._id}`}>
                        <div className="flex items-center gap-2 text-sm min-w-0">
                          <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                            label === 'Blocks' ? 'bg-destructive/15 text-destructive' :
                            label === 'Blocked by' ? 'bg-amber-500/15 text-amber-500' :
                            'bg-blue-500/15 text-blue-500'
                          }`}>{label}</span>
                          <ArrowRight className="h-3 w-3 text-muted-foreground shrink-0" />
                          <span className="truncate font-medium">{getItemTitle(otherId)}</span>
                        </div>
                        <button onClick={() => handleDeleteDep(dep._id)} className="opacity-0 group-hover:opacity-100 p-1 hover:bg-destructive/10 text-destructive rounded transition-opacity" data-testid={`delete-dep-${dep._id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {activeTab === 'comments' && (
            <div className="space-y-4">
              <form onSubmit={handleAddComment} className="flex gap-2">
                <input type="text" value={newComment} onChange={(e) => setNewComment(e.target.value)} placeholder="Add a comment..." className="flex-1 px-4 py-2.5 border border-input rounded-xl bg-background focus:outline-none focus:ring-2 focus:ring-ring text-sm" data-testid="comment-input" />
                <button type="submit" className="p-2.5 bg-primary text-primary-foreground rounded-xl hover:opacity-90 btn-press transition-opacity" data-testid="send-comment">
                  <Send className="h-4 w-4" />
                </button>
              </form>
              <div className="space-y-2">
                {comments.length === 0 ? (
                  <p className="text-center text-muted-foreground py-10 text-sm">No comments yet</p>
                ) : comments.map(c => (
                  <div key={c._id} className="p-3 bg-muted/30 rounded-xl border border-border" data-testid={`comment-${c._id}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium">{c.author}</span>
                      <span className="text-xs text-muted-foreground">{new Date(c.created_at).toLocaleString()}</span>
                    </div>
                    <p className="text-sm">{c.content}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'ai' && (
            <div className="space-y-5" data-testid="ai-tab-content">
              <div>
                <h3 className="text-sm font-semibold mb-1 flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-primary" /> AI Item Analysis
                </h3>
                <p className="text-xs text-muted-foreground mb-4">Get AI-powered insights, risk assessment, and improvement suggestions for this item.</p>
                <AIInsightsPanel itemId={item._id} />
              </div>

              <div className="border-t border-border pt-4">
                <h3 className="text-sm font-semibold mb-2 flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-primary" /> AI Description Generator
                </h3>
                <p className="text-xs text-muted-foreground mb-3">Auto-generate a description based on the item title. Switch to the Details tab to apply it.</p>
                <div className="p-4 bg-muted/30 rounded-xl border border-border">
                  <p className="text-sm font-medium mb-2">{item.title}</p>
                  <button
                    onClick={handleGenerateDescription}
                    disabled={generatingDesc}
                    className="w-full flex items-center justify-center gap-2 py-2.5 bg-primary text-primary-foreground rounded-lg hover:opacity-90 font-medium text-sm disabled:opacity-50"
                    data-testid="ai-generate-desc-button"
                  >
                    {generatingDesc ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                    Generate Description
                  </button>
                  {editedItem.description && (
                    <div className="mt-3 p-3 bg-background border border-border rounded-lg">
                      <p className="text-xs text-muted-foreground mb-1">Current description (auto-saved to Details tab):</p>
                      <p className="text-sm">{editedItem.description}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-card/95 backdrop-blur-md border-t border-border p-5 flex items-center justify-between">
          <button onClick={() => onDelete(item._id)} className="px-4 py-2 bg-destructive/10 text-destructive border border-destructive/20 rounded-xl hover:bg-destructive/20 transition-colors text-sm font-medium flex items-center gap-2" data-testid="delete-item">
            <Trash2 className="h-4 w-4" /> Delete
          </button>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 bg-secondary text-secondary-foreground rounded-xl hover:bg-secondary/80 text-sm font-medium transition-colors">Cancel</button>
            <button onClick={handleSave} className="px-5 py-2 bg-primary text-primary-foreground rounded-xl hover:opacity-90 font-semibold text-sm shadow-sm shadow-primary/20 btn-press transition-opacity" data-testid="save-item">Save Changes</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ItemDrawer;
